// AI_CONTEXT: {
//   "docs": [
//     "epics/202610091824_SMF_seat-map-filters/tickets/SMF-004-hide-resale-from-list-and-empty-state"
//   ],
//   "purpose": "Event page: loads the event and cart, renders the product list, and handles URL launch params (collections, access codes)",
//   "version": "1.0.0",
//   "priority": "core",
//   "stability": "stable"
// }

define("Pages/Event", [`Page`, `module`], function (Super, Self) {
	// shared between all instances

	let privateLinkEventId;

	return Super.inherit({
		namespace: Self.id,
		requires: [
			`Agent`,
			`AgentPriceCode`,
			`Countdown`,
			`Events`,
			`Groups`,
			`Products`,
			`ShoppingCart`,
			`System`,
			`User`,
			`XHR`,
		],
		async constructor(setup) {
			const { Agent, App, Config, Events, ShoppingCart, System, Time, XHR, container, data } = this;
			const { trackingOptions } = data;
			const { eventId, eventMode } = setup;
			const agentHandle = App.getCookie(`agentHandle`);
			const isAgentMode = this.isString(agentHandle, 1);
			const pltParam = App.getUrlParam(`plt`);
			const gstParam = App.getUrlParam(`gst`);
			const groupSaleUrlParam = App.getUrlParam(`gsurl`);
			const groupSaleId = App.getUrlParam(`gsId`);
			// ticket reservation token
			const trtParam = App.getUrlParam(`trt`);
			const tokenParam = App.getUrlParam(`stripe_redirect_token`);
			const params = {};
			const { isNativeApp } = System.get();
			const useWebBackButtonForApp = App.hasExternalCallback({ name: `onExitEvent` });
			const shouldCheckQueueIt = !trtParam;

			let { referrer } = document;
			let referralId = App.getUrlParam(`r`);
			let accessCode = App.getUrlParam(`ac`);
			let privateLinkToken = null;
			let groupSaleToken = gstParam;
			let promise;
			let event;
			let group;

			if (isNativeApp && useWebBackButtonForApp) {
				this.addClass(`native-app-back-button`);
			}

			if (!this.isString(referralId, 1)) {
				referralId = null;
			}

			if (!this.isString(referrer, 1)) {
				referrer = null;
			}

			if (!this.isString(accessCode, 1)) {
				accessCode = null;
			}

			if (isAgentMode) {
				this.addClass(`is-agent`);
			}

			this.assign(data, { agentHandle, eventMode, referralId, referrer });

			Events.setFetchMode(eventMode);

			// check for a Private Link Token
			if (this.isString(pltParam, 1)) {
				if (privateLinkEventId === undefined || privateLinkEventId === setup.eventId) {
					privateLinkEventId = eventId;
					privateLinkToken = pltParam;

					params.plt = privateLinkToken;
				}
			}

			// check for a Group Sales Token
			if (this.isString(gstParam, 1)) {
				groupSaleToken = gstParam;
				params.gst = gstParam;
			}

			// check for an Agent handle
			if (isAgentMode) {
				params.agent = agentHandle;
			}

			// check for queue-it expiration date
			const queueItCookies = App.getCookiesByPattern(`^tixr-queueit-${eventId}`);
			const queueItCookie = Object.values(queueItCookies)?.[0];
			if (this.isString(queueItCookie, 1) && shouldCheckQueueIt) {
				const params = new window.URLSearchParams(queueItCookie);
				const expirationStr = params.get(`expat`);

				if (expirationStr) {
					const expiration = parseInt(expirationStr);
					const timeDiff = Date.now() - expiration;
					const path = window.location.pathname;

					if (timeDiff >= 0) {
						window.location.reload();
					} else {
						this.Countdown.setExpiration({ expiration, id: `queue-it` });

						window.setTimeout(() => {
							if (path === window.location.pathname) {
								window.location.reload();
							}
						}, Math.abs(timeDiff));
					}
				}
			}

			await this.setupAgentPriceCode(eventId);

			promise = this.when({
				promises: [
					Events.get({
						by: `id`,
						force: true,
						id: eventId,
						params,
					}),
					ShoppingCart.init({
						accessCode,
						clear: false,
						eventId,
						privateLinkToken, // pass the plt into the cart init
						groupSaleId,
						groupSaleToken, // pass the gst into the cart init
						resumeCheckout: true, // resume checkout if one is already active
						groupSaleUrlParam,
					}),
				],
				spread: (_event) => {
					const { currencies, splb: serverPageLoadBehaviors } = _event;

					// e.g. if queue-it is required but they got here via clientside nav then reload
					if (this.isArray(serverPageLoadBehaviors, 1)) {
						const checkReloads = () => {
							serverPageLoadBehaviors.forEach(({ cookie, behavior }) => {
								const isQueueItCookie = cookie === `tixr-queueit-`;

								if (isQueueItCookie && !shouldCheckQueueIt) {
									return;
								}

								// queue it cookies are per-event
								const pattern = isQueueItCookie ? `^${cookie}${eventId}` : `^${cookie}`;
								const cookies = App.getCookiesByPattern(pattern);
								const cookieAsParams = Object.values(cookies).reduce((best, curCookie) => {
									const curParams = new window.URLSearchParams(curCookie || ``);

									if (best === null) {
										return curParams;
									}

									const expAt = parseInt(curParams.get(`expat`), 10);
									const bestExpAt = parseInt(best?.get(`expat`), 10);
									return expAt > bestExpAt ? curParams : best;
								}, null);
								const status = cookieAsParams?.get(`st`);

								if (behavior === `RELOAD` && (!status || status === `QUEUEIT_PURCHASE_COMPLETED`)) {
									window.location.reload();
								}
							});
						};

						if (window.location.pathname.match(/groups\/.*\/events\//)) {
							// URL is up to date, e.g. clicking back from non-event page
							checkReloads();
						} else {
							// URL hasn't updated yet, i.e. clicking an internal link
							this.App.onPageChange(checkReloads, { once: true });
						}
					}

					// if the event page does a clientside navigation like adding query params, then they click back
					// we need to check if a queue-it purchase was completed and server load to force them back in line
					this.App.onPageChange(() => {
						if (!shouldCheckQueueIt) {
							return;
						}

						const queueItCookies = App.getCookiesByPattern(`^tixr-queueit-${eventId}`);
						const queueItCookie = Object.values(queueItCookies)?.[0];

						if (this.isString(queueItCookie, 1)) {
							const params = new window.URLSearchParams(queueItCookie);
							const status = params.get(`st`);

							if (status === `QUEUEIT_PURCHASE_COMPLETED` && window.location.pathname.match(/groups\/.*\/events\//)) {
								window.location.reload();
							}
						}
					});

					event = _event;
					group = event.group;

					data.event = event;

					if (!isAgentMode && !data.isBenefitBannerInitialized) {
						this.User.get({
							subscribe: true,
							then: () => {
								this.initBenefitNotificationBanner({ groupId: group.id, eventId: event.id });
								data.isBenefitBannerInitialized = true;
							},
						});
					}

					ShoppingCart.get({
						subscribe: true,
						then: (cart) => {
							data.cart = cart;
							this.instances[`membership-cta`]?.setSuppressed({ suppressed: cart.quantity > 0 });
						},
					});

					if (this.isString(tokenParam, 1)) {
						ShoppingCart.checkRedirectOrderComplete({ purchaseFlow: `Purchase`, token: tokenParam });
					}

					// check for all in pricing modal
					if (event.feeInclusiveModal) {
						let message = `That means you'll see the cost of the ticket up-front including fees.`;

						if (event.orderLevelAdditionalFees > 0) {
							message = `That means you'll see the cost of the ticket up-front including fees. An additional order processing fee of ${Events.formatPrice(
								{
									price: event.orderLevelAdditionalFees,
									currency: currencies[0],
									type: `currencyCompact`,
								}
							)} may be added to each order.`;
						}

						if (event.hasSalesWithFlatShipping) {
							message += ` Any applicable shipping costs will be added at checkout.`;
						}

						//value, type, arg1, customClass, options = {}

						this.App.loadOverlay({
							type: `Message`,
							setup: {
								addHead: false,
								closable: false,
								heading: ` This event is using All-in Pricing.`,
								type: `confirmation`,
								message,
								yesLabel: `Accept & Continue`,
								noLabel: null,
								mobileModalStyle: `floating`,
								overlayMode: `modal`,
								style: `ux4`,
								size: `small`,
							},
						});
					}

					const { logoThumbnailUrl } = group;

					trackingOptions.pageType = `Event`;
					trackingOptions.eventId = event.id;
					data.uid = `e.${event.id}`;

					if (this.isString(logoThumbnailUrl)) {
						return XHR.loadImage({
							src: logoThumbnailUrl,
							then: (img) => {
								data.logoSrc = logoThumbnailUrl;
								data.logoHref = `/groups/${group.subdomain}`;
								data.logoAlt = `Tixr - ${group.name}`;
							},
							fail: (reason) => {
								/** swallow the error */
							},
						});
					}
				},
			});

			promise = this.when({
				promise,
				then: async () => {
					const currentTime = Time.getTime();
					const hashParams = App.getHashParams();
					const {
						backgroundUrl,
						backgroundUrlv2,
						hiddenDetailsList: eventHiddenDetailsList,
						id: eventId,
						group,
						mobileBackgroundImageUrl,
						mobileBackgroundImageUrlv2,
						venue,
					} = event;
					const { hiddenDetailsList: groupHiddenDetailsList } = group;
					const rightLinks = [];
					const leftLinks = [];
					const promises = [];
					const showCountdown = Boolean(this.Countdown.get()?.expiration);
					let customOpacity;
					let title;
					let desktopBackgroundUrl = backgroundUrlv2;
					let mobileBackgroundUrl = mobileBackgroundImageUrlv2;

					if (this.isString(backgroundUrl, 1) && !this.isString(backgroundUrlv2, 1)) {
						desktopBackgroundUrl = backgroundUrl;
						this.addClass(`legacy-bg-desktop`);
					}

					if (this.isString(mobileBackgroundImageUrl, 1) && !this.isString(mobileBackgroundImageUrlv2, 1)) {
						mobileBackgroundUrl = mobileBackgroundImageUrl;
						this.addClass(`legacy-bg-mobile`);
					}

					title = `${event.name} | Tickets at Your Computer or Mobile Device`;
					if (this.isObject(venue) && venue.venueType !== `VIRTUAL`) {
						title += ` at ${venue.name} in ${venue.address.city}`;
					}
					title += ` | ${event.group.name} | Tixr`;

					this.assign(data, { title });

					container.setAttribute(`eventCategoryName`, event.eventCategoryName);
					container.setAttribute(`layout`, event.layout);

					data.desktopBackgroundUrl = desktopBackgroundUrl;
					data.mobileBackgroundUrl = mobileBackgroundUrl;
					data.event = event;
					data.isFollowing = false;
					data.saleStarted = currentTime > event.saleWindowStart;
					data.multiday = event.endDate - event.startDate > 86400000; // 1000 ms * 60 sec * 60 min * 24 hr

					if (this.isString(event.theme)) {
						// NOTE: this overrides the default handling in Controller
						data.theme = event.theme;
						this.container.setAttribute(`theme`, event.theme);
					}

					this.chain(event.sales)
						.filter(this.Products.isActive)
						.tap((activeSales) => {
							this.assign(data, {
								currentTime: Time.getTime(),
								salesActive: activeSales.length > 0,
							});
						});

					if (!data.salesActive) {
						this.addClass(`inactive`);
					}

					this.render();

					if (this.isFinite(event.backgroundContrastPercentage, 0)) {
						customOpacity = event.backgroundContrastPercentage * 0.01;
					} else if (event.backgroundContrast) {
						customOpacity = 0.8;
					} else {
						customOpacity = 0;
					}
					this.get(`[name="background-contrast"]`).style.opacity = customOpacity;

					this.select(`.legal > p`).addClass(`small`);

					leftLinks.push({
						type: `icon`,
						customClass: `circle-background`,
						icon: `arrow-left`,
						mobile: true,
						desktop: this.isString(group.logoThumbnailUrl, 1),
						app: false,
						href: `/groups/${group.subdomain}`,
					});

					leftLinks.push({
						type: `icon-label`,
						customClass: `circle-background`,
						icon: `arrow-left`,
						label: `Back to ${group.name}`,
						mobile: false,
						desktop: !this.isString(group.logoThumbnailUrl, 1),
						app: false,
						href: `/groups/${group.subdomain}`,
					});

					if (this.isObject(event.rewardsPackage) && !this.includes(eventHiddenDetailsList, `REWARDS`)) {
						rightLinks.push({
							label: `Share`,
							ariaLabel: `Share to get rewards`,
							handler: (evt) =>
								App.loadOverlay({
									type: `Amplify`,
									setup: { eventId, header: `Rewards`, amplifyEvent: true },
								}),
							theme: `PINK`,
							type: `button`,
							icon: `plus`,
							mobile: false,
						});
						rightLinks.push({
							label: showCountdown ? undefined : `Share`,
							ariaLabel: showCountdown ? undefined : `Share to get rewards`,
							handler: (evt) =>
								App.loadOverlay({
									type: `Amplify`,
									setup: { eventId, header: `Rewards`, amplifyEvent: true },
								}),
							theme: `PINK`,
							type: showCountdown ? `icon` : `button`,
							icon: `plus`,
							desktop: false,
						});
					} else {
						rightLinks.push({
							label: `Share`,
							ariaLabel: `Share`,
							handler: (evt) =>
								App.loadOverlay({
									type: `Share`,
									setup: {
										eventId,
										type: `event`,
									},
								}),
							theme: `BLACK`,
							type: `button`,
							icon: `share-up`,
							mobile: false,
						});

						rightLinks.push({
							ariaLabel: `Share`,
							handler: (evt) =>
								App.loadOverlay({
									type: `Share`,
									setup: {
										eventId,
										type: `event`,
									},
								}),
							theme: `BLACK`,
							type: `icon`,
							icon: `share-up`,
							desktop: false,
						});
					}

					if (event.hacl === true) {
						// fixing merge issue - this should be of type `icon`. Theme: not used
						rightLinks.push({
							label: `Access Codes`,
							ariaLabel: `Access Codes`,
							icon: `lock-3`,
							handler: (evt) =>
								App.loadOverlay({
									setup: { eventId, scope: `event` },
									type: `EnterAccessCode`,
								}),
							type: `icon`,
						});
					}

					if (this.isString(data.logoSrc, 1)) {
						const { logoAlt, logoHref, logoSrc } = data;

						leftLinks.push({
							type: `image`,
							alt: logoAlt,
							href: logoHref,
							src: logoSrc,
							// only shown on desktop
							desktop: true,
							app: false,
							mobile: false,
						});
					}

					const mode = `ux3`;
					const showAvatar = !this.includes(eventHiddenDetailsList, `AVATAR`);
					const showSiteLinks = !this.includes(groupHiddenDetailsList, `FOOTER`);
					const intersectionContainerId = event.layout === `CAROUSEL` ? `page-header-intersection-id` : null;

					// page header needs the id for intersection observer
					await this.create({
						namespace: `Modules/EventProducts`,
						setup: {
							eventId,
							name: `products`,
						},
					}),
						promises.push(
							this.create({
								namespace: `Modules/EventInfo`,
								setup: {
									name: `event-info-content`,
									eventId,
									params,
								},
							}),
							this.create({
								namespace: `Modules/PageHeader`,
								setup: {
									name: `page-header`,
									desktopBackgroundWhenScrolled: ![`CAROUSEL`, `COLLECTION`].includes(event.layout),
									hideOnMobileScroll: true,
									intersectionContainerId,
									showAvatar,
									showTixrLogo: false,
									rightLinks,
									leftLinks,
									showUserLinks: false,
									callbacks: {
										onHideStateChange: ({ hidden }) => {
											if (hidden) {
												this.addClass(`header-hidden`, container);
											} else {
												this.removeClass(`header-hidden`, container);
											}
										},
									},
								},
							}),
							this.create({
								namespace: `Modules/ParallaxBlur`,
								setup: {
									name: `parallax-blur-desktop`,
									backgroundImageUrl: desktopBackgroundUrl,
									scrollContainer: this.container,
								},
							}),
							this.create({
								namespace: `Modules/ParallaxBlur`,
								setup: {
									name: `parallax-blur-mobile`,
									backgroundImageUrl: mobileBackgroundUrl,
									scrollContainer: this.container,
								},
							}),
							this.create({
								namespace: `Modules/PageFooter`,
								setup: { mode, name: `page-footer`, showSiteLinks },
							}),
							this.create({
								namespace: `Modules/ReadMore`,
								setup: {
									name: `description`,
									maxLinesMobile: 8,
									maxLinesDesktop: 8,
									content: event.description,
									heading: `Event Information`,
									textSizeMobile: `small`,
									textSizeDesktop: `large`,
								},
							}),
							this.create({
								namespace: `Modules/ReadMore`,
								setup: {
									name: `legal`,
									maxLinesMobile: 7,
									maxLinesDesktop: 3,
									content: event.legal,
									heading: `Terms`,
									textSizeMobile: `small`,
									textSizeDesktop: `small`,
								},
							})
						);

					if (this.isObject(event.rewardsPackage)) {
						if (hashParams.rewards !== undefined) {
							App.loadOverlay({
								type: `Amplify`,
								setup: { eventId, header: `Rewards`, amplifyEvent: true },
							});
						} else if (hashParams.points !== undefined) {
							// check for #points in the URL
							App.loadOverlay({
								type: `PointsList`,
								setup: { eventId },
							});
						}
					}

					await this.create({
						namespace: `Modules/CheckoutFooter`,
						setup: {
							name: `checkout-footer`,
							eventId,
							displayCountdownClock: false,
							// Site Header offset
							displayOffset: true,
							callbacks: {
								onToggleDisplay: (isDisplayed) => {
									const body = this.get(`.body`);

									if (isDisplayed) {
										this.removeClass(`checkout-footer-hide`, body);
									} else {
										this.addClass(`checkout-footer-hide`, body);
									}
								},
								zoom: ({ collectionId }) => {
									// zoom icon was clicked on a reserved sale
									App.loadOverlay({
										type: `ReservedCollection`,
										setup: { eventId, collectionId },
									});
								},
							},
						},
					});

					promises.push(
						this.create({
							namespace: `Modules/MembershipCta`,
							setup: {
								name: `membership-cta`,
								groupId: group.id,
								groupName: group.name,
								groupLogoUrl: group.logoThumbnailUrl || group.logoUrl,
								suppressed: data.cart?.quantity > 0,
								callbacks: {
									onCtaVisibilityChange: (isVisible) => {
										this.instances[`checkout-footer`]?.setFollowSuppressed({ suppressed: isVisible });

										// reserve bottom clearance so the fixed CTA doesn't cover page content
										if (isVisible) {
											this.addClass(`has-membership-cta`);
										} else {
											this.removeClass(`has-membership-cta`);
										}
									},
								},
							},
						})
					);

					if (isAgentMode) {
						promises.push(this.createAgentBanner({ eventId }));
					}

					// handle when the event page is opened in an iframe by Box Office to clear the shopping cart on close
					window.addEventListener(
						`message`,
						(evt) => {
							if (evt.data === `closing`) {
								Agent.clearSession();
								ShoppingCart.init();
							}
						},
						false
					);

					return this.when({ promises });
				},
			});

			promise = this.when({
				promise,
				then: async () => {
					const { collectionConfiguration } = event;
					const queryParams = App.getQueryParams();
					let hasAccessCodeParam = false;
					let hasFilterParams = false;
					let queryAction;
					let queryIds;

					this.each(queryParams, (value, key) => {
						if (!this.isString(value)) {
							return;
						}

						value = value.toUpperCase();
						key = key.toUpperCase();

						switch (key) {
							case `COL`:
								queryIds = value.split(`,`);
								queryIds = this.map(queryIds, (id) => this.parseInt(id));
								break;

							case `A`:
								queryAction = value;
								break;

							case `AC`:
								if (this.isString(value, 1)) {
									hasAccessCodeParam = true;
								}
								break;
							case `FILTER`: {
								const resaleFilterKey = this.Config.RESALE_FILTER_KEY.toUpperCase();
								const filterKeys = this.map(value.split(`|`), (filterParam) => filterParam.split(`:`)[0]);
								const nonResaleFilterKey = this.find(
									filterKeys,
									(filterKey) => this.isString(filterKey, 1) && filterKey !== resaleFilterKey
								);

								if (nonResaleFilterKey !== undefined) {
									hasFilterParams = true;
								}
								break;
							}
						}
					});

					if (!this.isString(data.cart.token)) {
						// make sure checkout isn't already in progress
						if (queryAction === `L`) {
							if (this.isString(groupSaleToken, 1)) {
								// Group Sales
								await App.loadOverlay({
									// await so the event page isn't displayed in the background before loading the overlay
									type: `ReservedCollection`,
									setup: {
										callbacks: {
											die: () => {
												if (data.cart.quantity === 0) {
													ShoppingCart.navigateToOriginalGroupSale();
												}
											},
										},
										eventId,
									},
								});
							} else if (this.isArray(queryIds, 1) && this.isObject(collectionConfiguration)) {
								const { collections } = collectionConfiguration;
								const [id] = queryIds;
								const collection = this.find(collections, { id });

								if (this.isObject(collection)) {
									// URL "launch" parameter is pointing to a valid collection
									const { layout } = collection;
									// launch the appropriate overlay based on whether reserved sales are present
									const type = this.includes(Config.RESERVED_COLLECTION_LAYOUTS, layout)
										? `ReservedCollection`
										: `Collection`;

									App.loadOverlay({
										type,
										setup: {
											callbacks: {
												die: () => {
													// clear any query params that may have been used to launch this
													App.setQueryParam(`A`, null);
													App.setQueryParam(`COL`, null);
													App.setQueryParam(`SID`, null);
												},
											},
											collectionId: id,
											eventId,
										},
										then: (collectionOverlay) => {
											if (event.hacl && !hasAccessCodeParam && hasFilterParams) {
												const collectionHacl = collectionOverlay.getTicketPicker().getHasAccessCodeLock();
												if (collectionHacl) {
													App.loadOverlay({
														setup: { eventId, scope: `event` },
														type: `EnterAccessCode`,
													});
												}
											}
										},
									});
								}
							}
						} else if (queryAction === `R`) {
							App.loadOverlay({
								type: `Amplify`,
								setup: { eventId, header: `Rewards`, amplifyEvent: true },
							});
							App.setQueryParam(`R`, null);
						}
					}
				},
			});

			return promise;
		},

		ready(setup) {
			const { App } = this;
			const { eventId } = setup;
			const eventType = `product-list-view`;

			// TXP-8406: fires after Page.js's ready() has dispatched page-view, so the much
			// larger view_item_list hit can never beat page_view to GA4's collect endpoint.
			App.captureInsight({ eventId, eventType });

			App.onScroll(() => {
				this.adjustCheckoutFooterPosition();
			});

			this.updateStickyHeaderOffset();
		},

		api: {
			/**
			 * Measures the sticky fan-preview-banner and blurb (if present/visible) and exposes their
			 * heights as CSS custom properties so the header, backgrounds, and blurb itself can stack
			 * beneath them without overlapping while they stay pinned to the top during scroll.
			 * @method updateStickyHeaderOffset
			 */
			updateStickyHeaderOffset: {
				value() {
					const bannerElement = this.get(`> .fan-preview-banner`);
					const blurbElement = this.get(`> .blurb`);
					const bannerHeight = this.isElement(bannerElement) ? bannerElement.offsetHeight : 0;
					const blurbHeight =
						this.isElement(blurbElement) && !blurbElement.classList.contains(`hidden`) ? blurbElement.offsetHeight : 0;

					this.container.style.setProperty(`--sticky-banner-height`, `${bannerHeight}px`);
					this.container.style.setProperty(`--sticky-header-offset`, `${bannerHeight + blurbHeight}px`);
				},
			},

			/**
			 * Adjusts the checkout footer position so it sits above the page footer when visible.
			 * Schedules via rAF to avoid bogging down the UI during scroll/resize.
			 * @method adjustCheckoutFooterPosition
			 * @param {boolean} [isResize=false] - If true, invalidates cached footer dimensions to account for height changes.
			 */
			adjustCheckoutFooterPosition: {
				value(isResize = false) {
					const { data } = this;

					if (data.adjustCheckoutFooterRafId) {
						window.cancelAnimationFrame(data.adjustCheckoutFooterRafId);
					}

					data.adjustCheckoutFooterRafId = window.requestAnimationFrame(() => {
						if (isResize) {
							data.pageFooterDimensions = null;
						}

						this.applyCheckoutFooterTransform();

						// run once more on the next frame to handle initial load/fast scroll/resize settling
						window.requestAnimationFrame(() => {
							this.applyCheckoutFooterTransform();
						});
					});
				},
			},

			/**
			 * Performs the footer transform calculations and applies the necessary CSS transform to the checkout footer.
			 * @method applyCheckoutFooterTransform
			 */
			applyCheckoutFooterTransform: {
				value() {
					const { data, elements } = this;
					const { DOM, System } = this;
					const isMobile = System.get().breakpoint === `mobile`;

					if (!data.pageFooterDimensions) {
						data.pageFooterDimensions = DOM.getDimensions(this.get(`.content.footer`));
					}

					if (!elements.checkoutFooterContainer) {
						elements.checkoutFooterContainer = this.get(`[name="checkout-footer"]`);
					}

					let newGap = 0;

					if (!isMobile) {
						const { viewportBottom } = this.dimensions;
						const { height: footerHeight } = data.pageFooterDimensions;
						const difference = footerHeight + viewportBottom;

						if (difference > 0) {
							newGap = difference;
						}
					}

					if (newGap !== data.lastCheckoutFooterGap) {
						elements.checkoutFooterContainer.style.bottom = `calc(var(--desktop-side-spacing) + ${newGap}px)`;
						data.lastCheckoutFooterGap = newGap;
					}
				},
			},

			/**
			 * Does the work to enable agent checkout to select price codes.
			 * Agent checkout price codes are only available via the agent check out iframe and if the parent frame passes the required query parameter in the url.
			 * The url should contain the `acpc` query parameter with a value of `1`.
			 * @method AgentPriceCode#setupAgentPriceCode
			 * @param {Object} setup - setup object
			 */
			setupAgentPriceCode: {
				async value(eventId) {
					const { AgentPriceCode, App } = this;
					const agentCheckoutPriceCodeParam = App.getUrlParam(`acpc`);
					const allowAgentPriceCode = agentCheckoutPriceCodeParam === `1` && App.isInAgentCheckoutIframe();

					AgentPriceCode.setEvent({ eventId, enabled: allowAgentPriceCode });

					// only check for price codes if it is in agent mode and inside an iframe
					if (allowAgentPriceCode) {
						await AgentPriceCode.getPriceCodes();

						const defaultPriceCode = AgentPriceCode.getDefaultPriceCode();

						if (defaultPriceCode) {
							AgentPriceCode.set(defaultPriceCode);
						}
					}
				},
			},

			/**
			 * Creates the agent banner module.
			 * @method createAgentBanner
			 */
			createAgentBanner: {
				async: true,
				value({ eventId }) {
					return this.create({
						namespace: `Modules/AgentBanner`,
						setup: {
							name: `agent-banner`,
							container: this.get(`[name="agent-banner"]`),
							eventId,
							allowShowAgent: true,
							allowShowPriceCodes: this.AgentPriceCode.isEnabled(), // show the price codes selector if the event is using price books
						},
					});
				},
			},

			createMediaForCarousel: {
				value() {
					const { event } = this.data;
					const { videoId } = event;
					let allMedia = this.clone(event.media);
					let video;

					if (this.isString(videoId, 1)) {
						video = {
							id: -1,
							ordinal: -1,
							target: `GALLERY`,
							type: `VIDEO`,
							videoId,
						};

						if (this.isArray(allMedia, 1)) {
							allMedia.unshift(video);
						} else {
							allMedia = [video];
						}
					}

					if (this.isArray(allMedia, 1)) {
						this.chain(allMedia)
							.sortBy(`ordinal`)
							.tap(function (media) {
								allMedia = media;
							});

						const alignWithElement = this.get(`.gallery > h2`);

						this.lazyCreate({
							namespace: `Modules/ImageGallery`,
							setup: {
								name: `image-gallery`,
								media: allMedia,
								layout: `horizontal`,
								mode: `highlight`,
								alignWithElement,
							},
						});
					} else {
						this.hide({ target: this.get(`.gallery`) });
					}
				},
			},

			createMediaForGrid: {
				value() {
					const { event } = this.data;
					const { videoId } = event;
					const videoElement = this.get(`.content.video`);

					if (this.isArray(event.media, 1)) {
						this.chain(event.media)
							.sortBy(`ordinal`)
							.tap(function (media) {
								event.media = media;
							});

						this.lazyCreate({
							namespace: `Modules/ImageGallery`,
							setup: {
								name: `image-gallery`,
								media: event.media,
							},
						});
					} else {
						this.hide({ target: this.get(`.gallery`) });
					}

					if (this.isString(videoId, 1)) {
						this.getVideoThumbnail({
							videoId,
							then: (imageElement) => {
								// thumbnail loaded successfully
								const { src } = imageElement;
								const thumbnailElement = this.get(`.background.thumbnail`, videoElement);

								thumbnailElement.style.backgroundImage = `url("${src}")`;
							},
							fail: (reason) => {
								// error loading thumbnail
								const type = `warning`;
								const message = `Error loading YouTube thumbnail for video ${videoId}`;

								this.log({ message, type });

								// create the embedded player
								this.createVideoPlayer({ autoplay: false });
							},
							fin: () =>
								this.show({
									target: videoElement,
									transition: `height`,
								}),
						});
					}
				},
			},

			getVideoThumbnail: {
				async: true,
				value({ videoId }) {
					const MIN_YOUTUBE_THUMBNAIL_HEIGHT = 200;
					const { XHR } = this;
					const srcPrefix = `//img.youtube.com/vi`;
					const srcSuffixes = [`maxresdefault.jpg`, `hqdefault.jpg`];
					let checkImage;
					let promise;
					let src;

					checkImage = (element) => {
						if (this.isElement(element)) {
							// assume the 404 response if image is very small
							if (this.isFinite(element.height, MIN_YOUTUBE_THUMBNAIL_HEIGHT)) {
								return true;
							}
						}

						return false;
					};

					this.each(srcSuffixes, (srcSuffix) => {
						const onError = () => {
							src = `${srcPrefix}/${videoId}/${srcSuffix}`;
							return XHR.loadImage({ src });
						};

						promise = this.when({
							promise,
							then: (image) => {
								if (checkImage(image)) {
									return image;
								} else {
									return onError();
								}
							},
							fail: onError,
						});
					});

					return this.when({
						promise,
						then: (image) => {
							if (checkImage(image)) {
								return image;
							} else {
								throw new Error(`Couldn't load YouTube thumbnail for id "${videoId}"`);
							}
						},
					});
				},
			},

			createVideoPlayer: {
				defaults: {
					autoplay: true,
				},
				value({ autoplay }) {
					const { data } = this;
					const { event } = data;
					const { videoId } = event;
					const name = `video-player`;

					this.create({
						namespace: `Modules/VideoPlayer`,
						setup: { autoplay, name, videoId },
						then: () => {
							const target = this.get(`.content.video`);
							this.addClass(`playing`, target);
						},
					});
				},
			},

			getCountDown: {
				value() {
					const { data, Time } = this;
					const { event } = data;
					const { sales, saleWindowStart, venue } = event;
					// find a sale that should be shown
					const activeSale = this.find(sales, this.Products.isActive);
					const hasActiveSale = this.isObject(activeSale);
					const now = Time.getTime();
					const isSaleStarted = now > saleWindowStart;

					// Carousel cards have this information in their own badge
					if (hasActiveSale || isSaleStarted) {
						this.getCountDownToSaleEnd();
					} else {
						data.countdown = `On sale ${Time.format(`%W %m %d`, saleWindowStart, venue.timezone)} at ${Time.format(
							`%H:%i%a %t`,
							saleWindowStart,
							venue.timezone
						)}`;
					}
				},
			},

			getCountDownToSaleEnd: {
				value() {
					const { data, Time } = this;
					const { event } = data;
					const { venue } = event;
					const currentTime = Time.getTime();
					const timeLeft = event.startDate - currentTime;
					const SECOND = 1000;
					const MINUTE = SECOND * 60;
					const HOUR = MINUTE * 60;
					const DAY = HOUR * 24;
					let count;
					let label;

					if (!this.includes(event.hiddenDetailsList, `DATE`)) {
						if (timeLeft > DAY) {
							count = Time.daysUntil(event.startDate, venue.timezone);
							const isPlural = this.isInt(count) && count !== 1;
							label = isPlural ? `days` : `day`;
						} else if (!this.includes(event.hiddenDetailsList, `TIME`)) {
							if (timeLeft > HOUR) {
								count = Math.floor(timeLeft / HOUR);
								const isPlural = this.isInt(count) && count !== 1;
								label = isPlural ? `hours` : `hour`;
							} else if (timeLeft > MINUTE) {
								count = Math.floor(timeLeft / MINUTE);
								const isPlural = this.isInt(count) && count !== 1;
								label = isPlural ? `minutes` : `minute`;
							} else if (timeLeft > SECOND) {
								count = Math.floor(timeLeft / SECOND);
								const isPlural = this.isInt(count) && count !== 1;
								label = isPlural ? `seconds` : `second`;
							}
						}
					}

					if (count !== undefined) {
						data.countdown = `${count} ${label} to event`;
					}
				},
			},

			/**
			 * Updates the agent banner with the selected hold's price code based on the selected hold.
			 * If the hold has a price code override (isPriceCodeEditable is false), the banner will show a temporarily locked price code.
			 * If the hold does not have a price code override, the banner will allow the agent to select a price code.
			 * @method updateAgentBannerPriceCode
			 * @param {Number} targetHoldId - The ID of the hold
			 */
			updateAgentBannerPriceCode: {
				value(targetHoldId) {
					const { instances, data } = this;
					const agentBanner = instances[`agent-banner`];

					if (this.isObject(agentBanner)) {
						// update the agent banner with the selected hold
						if (this.isInt(targetHoldId)) {
							const holdFilterOption = this.find(data.holdFilterOptions, { value: targetHoldId });

							if (holdFilterOption?.isPriceCodeEditable === false) {
								agentBanner.setTempPriceCode({
									id: holdFilterOption.priceListId,
									code: holdFilterOption.priceCode,
								});
							} else {
								agentBanner.removeTempPriceCode();
							}
						} else {
							agentBanner.removeTempPriceCode();
						}
					}
				},
			},

			/**
			 * Checks the user's benefits for the event and sets a timeout to show a notification banner when the next benefit becomes active.
			 * The method fetches the user's benefits for the specified event and group, finds the soonest benefit start date that is in the future, and sets a timeout to show a notification banner when that start date is reached.
			 * @method initBenefitNotificationBanner
			 * @param {Object} params - The parameters object
			 * @param {Number} params.groupId - The ID of the group
			 * @param {Number} params.eventId - The ID of the event
			 * @example
			 * // Check for upcoming benefits and show a notification banner when they become active
			 * Controller.initBenefitNotificationBanner({ groupId: 123, eventId: 456 });
			 */
			initBenefitNotificationBanner: {
				async value({ groupId, eventId }) {
					if (!this.isInt(groupId)) {
						throw new Error(`Group ID is required`);
					}

					if (!this.isInt(eventId)) {
						throw new Error(`Event ID is required`);
					}

					// return if no logged-in user
					const user = await this.User.get().catch(() => null);
					if (!this.isObject(user)) {
						return;
					}

					const { Events } = this;
					const benefits = await Events.getUserBenefitsByEvent({ groupId, eventId }).catch(() => {
						this.log({ type: `error`, message: `Failed to fetch user benefits` });
					});

					let soonestBenefitStartDate = null;

					this.each(benefits, (benefit) => {
						if (benefit.status === `ACTIVE` && benefit.startDate > Date.now()) {
							if (soonestBenefitStartDate === null || benefit.startDate < soonestBenefitStartDate) {
								soonestBenefitStartDate = benefit.startDate;
							}
						}
					});

					if (soonestBenefitStartDate !== null) {
						const timeUntilNextBenefit = soonestBenefitStartDate - Date.now();
						window.setTimeout(() => {
							this.showBenefitNotificationBanner();
						}, timeUntilNextBenefit);
					}
				},
			},

			/**
			 * Shows a banner that notifies the user that a benefit is now active and they should refresh the page to unlock tickets.
			 * The banner is hidden by default and only shown when the showBenefitNotificationBanner method is called, which is triggered by a timeout based on the next benefit's start date.
			 * The banner includes a "Refresh" link that reloads the page when clicked.
			 * @method showBenefitNotificationBanner
			 * @example
			 * // show the banner immediately
			 * Controller.getBenefitNotificationBanner().then(() => {
			 *   Controller.showBenefitNotificationBanner();
			 * });
			 * @example
			 * // show the banner based on the next benefit's start date
			 * Controller.getBenefitNotificationBanner({ groupId: 123, eventId: 456 });
			 *
			 */
			showBenefitNotificationBanner: {
				value() {
					this.App.notifyUser({
						type: `warning`,
						message: `<span style="text-align: center; width:100%;">Your benefit is now active!<span class="hide-desktop"><br/></span>&nbsp;<a href="#" onclick="event.preventDefault(); window.location.reload()">Refresh</a> to unlock tickets.</span>`,
						duration: 0, // persistent — does not auto-dismiss
					});
				},
			},
		},

		destructor(options) {
			const { App, data } = this;

			// clean up the next scheduled animation frame
			if (data.adjustCheckoutFooterRafId) {
				window.cancelAnimationFrame(data.adjustCheckoutFooterRafId);
			}

			// clear any event page query params
			App.setQueryParam(`A`, null);
			App.setQueryParam(`COL`, null);
			App.setQueryParam(`SID`, null);
		},

		listeners: [
			{
				event: `click`,
				filter: `[action]`,
				handler(evt) {
					const { App, DOM } = this;
					const { currentTarget } = evt;
					const action = currentTarget.getAttribute(`action`);

					switch (action) {
						case `close-blurb`:
							this.addClass(`hidden`, this.get(`> .blurb`));
							this.updateStickyHeaderOffset();
							DOM.trigger({ event: `resize`, target: window });
							return;

						case `play-video`:
							return this.createVideoPlayer({ autoplay: true });

						case `backToApp`:
							return App.callExternal({
								name: `onExitEvent`,
							});
					}
				},
			},
			{
				event: `resize`,
				target: window,
				matchContainer: false,
				handler() {
					this.adjustCheckoutFooterPosition(true);
					this.updateStickyHeaderOffset();
				},
			},
		],
	});
});

define("Pages/Event.templates",["handlebars"], function(Handlebars) {

this["Templates"] = this["Templates"] || {};

this["Templates"]["Pages/Event/main"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return " <div name=\"agent-banner\"></div> ";
},"3":function(container,depth0,helpers,partials,data) {
    return " <div class=\"fan-preview-banner\"> <div>This is a demo. Tickets go on sale soon.</div> </div> ";
},"5":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"shortBlurb") : depth0),{"name":"if","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":218},"end":{"line":1,"column":434}}})) != null ? stack1 : "")
    + "  <header class=\"head-fixed\"> <div name=\"page-header\" class=\"hide-app hide-agent "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (data && lookupProperty(data,"root"))) && lookupProperty(stack1,"showLogo")),{"name":"if","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":553},"end":{"line":1,"column":591}}})) != null ? stack1 : "")
    + "\"></div> </header> <a role=\"button\" href=\"#\" action=\"backToApp\" aria-label=\"Back\" class=\"button ux4 icon tertiary\" theme=\""
    + alias2(container.lambda((depth0 != null ? lookupProperty(depth0,"theme") : depth0), depth0))
    + "\"> "
    + alias2(lookupProperty(helpers,"getIcon").call(alias1,"arrow-left",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":725},"end":{"line":1,"column":749}}}))
    + " </a> <div name=\"parallax-blur-desktop\" class=\"hide-mobile\" role=\"region\" aria-hidden=\"true\" ></div> <div name=\"parallax-blur-mobile\" class=\"hide-desktop\" role=\"region\" aria-hidden=\"true\" ></div> <div name=\"background-contrast\" class=\"hide-mobile\"></div> <div name=\"background-blur\"></div> <div name=\"background-gradient\"></div> <main class=\"body checkout-footer-hide\"> <div class=\"content\" name=\"products\"></div> <div name=\"event-info-content\" /> </main> <div name=\"checkout-footer\" role=\"region\" aria-label=\"Checkout\"></div> <div name=\"membership-cta\"></div> <footer class=\"content footer\"> <div name=\"page-footer\"></div> </footer> ";
},"6":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"blurb\" role=\"region\" aria-label=\"Event Banner\"> <p>"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"shortBlurb") : depth0), depth0)) != null ? stack1 : "")
    + "</p> <a role=\"button\" href=\"#\" action=\"close-blurb\" aria-label=\"Dismiss banner\">"
    + container.escapeExpression(lookupProperty(helpers,"getIcon").call(depth0 != null ? depth0 : (container.nullContext || {}),"close",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":396},"end":{"line":1,"column":415}}}))
    + "</a> </div> ";
},"8":function(container,depth0,helpers,partials,data) {
    return " has-logo";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"agentHandle") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":59}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"eventMode") : depth0),"FAN_PREVIEW",{"name":"compare","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":60},"end":{"line":1,"column":201}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"with").call(alias1,(depth0 != null ? lookupProperty(depth0,"event") : depth0),{"name":"with","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":202},"end":{"line":1,"column":1392}}})) != null ? stack1 : "");
},"useData":true});

return this["Templates"];

});
//# sourceMappingURL=Event.js.map